import React, {useEffect, useRef, useState, useCallback} from 'react'
import {Modal} from 'react-bootstrap'
import {ProductPlan} from '../../../../services/cellcast.v2.types'
import {useNavigate} from 'react-router-dom'
import './PaymentSuccessModal.scss'

interface PaymentSuccessModalProps {
  show: boolean
  onClose: () => void
  plan: ProductPlan | null
  onClosed?: () => void
  onGoToSubscriptions?: () => void
  description: string
  titleGoto?: string
  hideActions?: boolean
}

const PaymentSuccessModal: React.FC<PaymentSuccessModalProps> = ({
  show,
  onClose,
  plan,
  description,
  onClosed,
  onGoToSubscriptions,
  titleGoto = 'Manage your plan',
  hideActions = false,
}) => {
  const timeToClose = 5
  const confettiContainerRef = useRef<HTMLDivElement>(null)
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [countdown, setCountdown] = useState(timeToClose)

  const handleBack = () => {
    // closed modal
    onClose()
    if (onClosed) {
      onClosed()
    }
  }

  const handleGoTo = useCallback(() => {
    onClose()
    onGoToSubscriptions?.()
  }, [onClose, onGoToSubscriptions])

  const createConfetti = () => {
    if (!confettiContainerRef.current) return

    const container = confettiContainerRef.current
    const shapes = ['square', 'rect', 'circle', 'triangle']
    const colors = [
      '#FFD700',
      '#FF69B4',
      '#00CED1',
      '#9370DB',
      '#32CD32',
      '#FF6347',
      '#4169E1',
      '#FFA500',
      '#20B2AA',
      '#DA70D6',
    ]
    const animations = ['confetti-fall-1', 'confetti-fall-2', 'confetti-fall-3', 'confetti-fall-4']

    for (let i = 0; i < 60; i++) {
      const confetti = document.createElement('div')
      const shapeClass = shapes[Math.floor(Math.random() * shapes.length)]
      const animationClass = animations[Math.floor(Math.random() * animations.length)]

      confetti.className = `confetti confetti-${shapeClass}`
      confetti.style.color = colors[Math.floor(Math.random() * colors.length)]
      confetti.style.left = Math.random() * 100 + '%' // Keep within modal bounds
      confetti.style.top = Math.random() * 50 - 100 + 'px' // Random starting height
      confetti.style.animationDelay = Math.random() * 0.5 + 's' // Quick burst - all within 0.5s
      confetti.style.animationDuration = 3 + Math.random() * 2 + 's' // 3-5 second fall
      confetti.style.animationName = animationClass
      confetti.style.animationIterationCount = '1'
      confetti.style.animationFillMode = 'forwards'
      confetti.style.zIndex = '1000'
      container.appendChild(confetti)

      setTimeout(() => {
        if (confetti.parentNode) {
          confetti.parentNode.removeChild(confetti)
        }
      }, 8000)
    }
  }

  const createAdditionalConfetti = () => {
    if (!confettiContainerRef.current) return

    const container = confettiContainerRef.current
    const shapes = ['square', 'rect', 'circle', 'triangle']
    const colors = [
      '#FFD700',
      '#FF69B4',
      '#00CED1',
      '#9370DB',
      '#32CD32',
      '#FF6347',
      '#4169E1',
      '#FFA500',
    ]
    const animations = ['confetti-fall-1', 'confetti-fall-2', 'confetti-fall-3', 'confetti-fall-4']

    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        const confetti = document.createElement('div')
        const shapeClass = shapes[Math.floor(Math.random() * shapes.length)]
        const animationClass = animations[Math.floor(Math.random() * animations.length)]

        confetti.className = `confetti confetti-${shapeClass}`
        confetti.style.color = colors[Math.floor(Math.random() * colors.length)]
        confetti.style.left = Math.random() * 100 + '%'
        confetti.style.top = Math.random() * 50 - 100 + 'px'
        confetti.style.animationDelay = Math.random() * 1 + 's' // Slower timing
        confetti.style.animationDuration = 4 + Math.random() * 3 + 's' // Slower fall (4-7 seconds)
        confetti.style.animationName = animationClass
        confetti.style.animationIterationCount = '1'
        confetti.style.animationFillMode = 'forwards'
        confetti.style.zIndex = '1000'
        container.appendChild(confetti)

        setTimeout(() => {
          if (confetti.parentNode) {
            confetti.parentNode.removeChild(confetti)
          }
        }, 10000)
      }, i * 800)
    }
  }

  const createSlowConfetti = () => {
    if (!confettiContainerRef.current) return

    const container = confettiContainerRef.current
    const shapes = ['square', 'rect', 'circle', 'triangle']
    const colors = [
      '#FFD700',
      '#FF69B4',
      '#00CED1',
      '#9370DB',
      '#32CD32',
      '#FF6347',
      '#4169E1',
      '#FFA500',
    ]
    const animations = ['confetti-fall-1', 'confetti-fall-2', 'confetti-fall-3', 'confetti-fall-4']

    // Create just 1-2 pieces for slow effect
    const numPieces = Math.floor(Math.random() * 2) + 1

    for (let i = 0; i < numPieces; i++) {
      const confetti = document.createElement('div')
      const shapeClass = shapes[Math.floor(Math.random() * shapes.length)]
      const animationClass = animations[Math.floor(Math.random() * animations.length)]

      confetti.className = `confetti confetti-${shapeClass}`
      confetti.style.color = colors[Math.floor(Math.random() * colors.length)]
      confetti.style.left = Math.random() * 100 + '%'
      confetti.style.top = Math.random() * 50 - 100 + 'px'
      confetti.style.animationDelay = Math.random() * 0.5 + 's'
      confetti.style.animationDuration = 5 + Math.random() * 4 + 's' // Very slow fall (5-9 seconds)
      confetti.style.animationName = animationClass
      confetti.style.animationIterationCount = '1'
      confetti.style.animationFillMode = 'forwards'
      confetti.style.zIndex = '1000'
      container.appendChild(confetti)

      // Remove confetti after animation
      setTimeout(() => {
        if (confetti.parentNode) {
          confetti.parentNode.removeChild(confetti)
        }
      }, 12000)
    }
  }

  useEffect(() => {
    if (show) {
      // Clear existing confetti first
      if (confettiContainerRef.current) {
        confettiContainerRef.current.innerHTML = ''
      }

      // Create initial big burst immediately
      createConfetti()

      // Create additional burst after 1 second
      setTimeout(createAdditionalConfetti, 1000)

      // Create continuous slow confetti every 2-3 seconds
      const slowConfettiInterval = setInterval(() => {
        createSlowConfetti()
      }, 2000 + Math.random() * 1000) // 2-3 seconds interval

      // Clean up interval when modal closes
      return () => {
        clearInterval(slowConfettiInterval)
      }
    }
  }, [show])

  useEffect(() => {
    if (show && hideActions) {
      setCountdown(timeToClose)

      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
      }

      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current)
              countdownIntervalRef.current = null
            }
            handleGoTo()
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current)
          countdownIntervalRef.current = null
        }
      }
    } else {

      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
      setCountdown(timeToClose)
    }
  }, [show, hideActions, handleGoTo, timeToClose])

  if (!plan) return null

  return (
    <Modal show={show} onHide={onClose} centered size='lg' className='payment-success-modal'>
      <Modal.Body className='modal-body'>
        {/* Confetti */}
        <div ref={confettiContainerRef} className='confetti-container'>
          <div
            className='confetti confetti-square'
            style={{
              left: '5%',
              top: '-80px',
              color: '#FFD700',
              animationName: 'confetti-fall-1',
              animationDelay: '0s',
            }}
          ></div>
          <div
            className='confetti confetti-circle'
            style={{
              left: '25%',
              top: '-60px',
              color: '#FF69B4',
              animationName: 'confetti-fall-2',
              animationDelay: '0.3s',
            }}
          ></div>
          <div
            className='confetti confetti-triangle'
            style={{
              left: '45%',
              top: '-100px',
              color: '#00CED1',
              animationName: 'confetti-fall-3',
              animationDelay: '0.6s',
            }}
          ></div>
          <div
            className='confetti confetti-rect'
            style={{
              left: '65%',
              top: '-70px',
              color: '#9370DB',
              animationName: 'confetti-fall-4',
              animationDelay: '0.9s',
            }}
          ></div>
          <div
            className='confetti confetti-square'
            style={{
              left: '85%',
              top: '-90px',
              color: '#32CD32',
              animationName: 'confetti-fall-1',
              animationDelay: '1.2s',
            }}
          ></div>
          <div
            className='confetti confetti-circle'
            style={{
              left: '15%',
              top: '-50px',
              color: '#FF6347',
              animationName: 'confetti-fall-2',
              animationDelay: '1.5s',
            }}
          ></div>
          <div
            className='confetti confetti-triangle'
            style={{
              left: '35%',
              top: '-110px',
              color: '#4169E1',
              animationName: 'confetti-fall-3',
              animationDelay: '1.8s',
            }}
          ></div>
          <div
            className='confetti confetti-rect'
            style={{
              left: '55%',
              top: '-75px',
              color: '#FFA500',
              animationName: 'confetti-fall-4',
              animationDelay: '2.1s',
            }}
          ></div>
          <div
            className='confetti confetti-square'
            style={{
              left: '75%',
              top: '-85px',
              color: '#20B2AA',
              animationName: 'confetti-fall-1',
              animationDelay: '2.4s',
            }}
          ></div>
          <div
            className='confetti confetti-circle'
            style={{
              left: '95%',
              top: '-65px',
              color: '#DA70D6',
              animationName: 'confetti-fall-2',
              animationDelay: '2.7s',
            }}
          ></div>
        </div>

        <div className='success-icon-wrapper'>
          <svg
            className='business-card-icon'
            viewBox='0 0 100 100'
            xmlns='http://www.w3.org/2000/svg'
          >
            {/* Business Card Background */}
            <rect
              x='15'
              y='25'
              width='70'
              height='45'
              rx='4'
              fill='#ffffff'
              stroke='#e0e0e0'
              strokeWidth='1'
            />

            {/* Card Shadow */}
            <rect x='16' y='26' width='70' height='45' rx='4' fill='#f5f5f5' opacity='0.5' />

            {/* Profile Icon */}
            <circle cx='30' cy='40' r='8' fill='#4A90E2' />
            <circle cx='30' cy='36' r='4' fill='#ffffff' />
            <rect x='26' y='44' width='8' height='6' rx='2' fill='#ffffff' />

            {/* Name Line */}
            <rect x='45' y='35' width='35' height='3' rx='1.5' fill='#333333' />

            {/* Title Line */}
            <rect x='45' y='42' width='25' height='2' rx='1' fill='#666666' />

            {/* Contact Info */}
            <circle cx='48' cy='52' r='1.5' fill='#4A90E2' />
            <rect x='52' y='51' width='15' height='2' rx='1' fill='#888888' />

            {/* QR Code Placeholder */}
            <rect
              x='65'
              y='52'
              width='12'
              height='12'
              fill='#f0f0f0'
              stroke='#d0d0d0'
              strokeWidth='0.5'
            />
            <rect x='67' y='54' width='2' height='2' fill='#333333' />
            <rect x='71' y='54' width='2' height='2' fill='#333333' />
            <rect x='67' y='58' width='2' height='2' fill='#333333' />
            <rect x='73' y='58' width='2' height='2' fill='#333333' />
            <rect x='67' y='62' width='2' height='2' fill='#333333' />
            <rect x='71' y='62' width='2' height='2' fill='#333333' />
          </svg>
        </div>

        {/* Success Message */}
        <h2 className='modal-title'>Your LeadzApp subscription is ready!</h2>
        <p className='modal-description'>
          {description}
          <br />
        </p>

        {!hideActions && (
        <div className='action-buttons'>
          <button className='btn-action' onClick={handleBack}>
            <i className='bi bi-house'></i>
            <span>Close</span>
          </button>

          <button className='btn-action' onClick={handleGoTo}>
            <i className='bi bi-credit-card'></i>
            <span>{titleGoto}</span>
          </button>
        </div>
        )}

        {hideActions && (
          <div className='action-buttons'>
            <button className='btn-action' onClick={handleGoTo}>
              <i className='bi bi-arrow-right'></i>
              <span>Next ({countdown}s)</span>
            </button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default PaymentSuccessModal
